import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { paymentService } from '@/services/payment.service'
import type { PaymentMethod } from '@/types/checkout'
import type { PixPayment, BoletoPayment, CreditCardPayment } from '@/types/payment'

export interface UsePaymentOptions {
  orderId: string
  onSuccess?: (data: any) => void
  onError?: (error: any) => void
}

export interface UsePaymentReturn {
  // State
  paymentMethod: PaymentMethod | null
  paymentData: PixPayment | BoletoPayment | CreditCardPayment | null
  isProcessing: boolean

  // Actions
  setPaymentMethod: (method: PaymentMethod) => void
  createPixPayment: () => Promise<PixPayment>
  createBoletoPayment: () => Promise<BoletoPayment>
  processCreditCard: (data: any) => Promise<CreditCardPayment>
}

export function usePayment({
  orderId,
  onSuccess,
  onError,
}: UsePaymentOptions): UsePaymentReturn {
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null)
  const [paymentData, setPaymentData] = useState<any>(null)

  // Criar pagamento PIX
  const pixMutation = useMutation({
    mutationFn: async () => paymentService.createPixPayment(orderId),
    onSuccess: (data) => {
      setPaymentData(data)
      onSuccess?.(data)
    },
    onError: (error) => {
      onError?.(error)
    },
  })

  // Criar boleto
  const boletoMutation = useMutation({
    mutationFn: async () => paymentService.createBoleto(orderId),
    onSuccess: (data) => {
      setPaymentData(data)
      onSuccess?.(data)
    },
    onError: (error) => {
      onError?.(error)
    },
  })

  // Processar cartão de crédito
  const creditCardMutation = useMutation({
    mutationFn: async (data: any) => paymentService.processCreditCard(data),
    onSuccess: (data) => {
      setPaymentData(data)
      onSuccess?.(data)
    },
    onError: (error) => {
      onError?.(error)
    },
  })

  return {
    // State
    paymentMethod,
    paymentData,
    isProcessing: pixMutation.isPending || boletoMutation.isPending || creditCardMutation.isPending,

    // Actions
    setPaymentMethod,
    createPixPayment: pixMutation.mutateAsync,
    createBoletoPayment: boletoMutation.mutateAsync,
    processCreditCard: creditCardMutation.mutateAsync,
  }
}
